/**
 * Settings Navigation Card Component
 *
 * A clickable card for navigating to settings sub-sections.
 * Used in the Settings hub page.
 */
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { cn, Card, CardContent } from '@respectly/ui';
import { ChevronRight, type LucideIcon } from 'lucide-react';

// ============================================
// Component Props
// ============================================

export interface SettingsNavigationCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  to: string;
  iconColor?: string;
  iconBgColor?: string;
  className?: string;
}

// ============================================
// Component
// ============================================

export function SettingsNavigationCard({
  icon: Icon,
  title,
  description,
  to,
  iconColor = 'text-violet-600 dark:text-violet-400',
  iconBgColor = 'bg-violet-100 dark:bg-violet-900/40',
  className,
}: SettingsNavigationCardProps) {
  return (
    <Link to={to}>
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <Card
          className={cn(
            'relative overflow-hidden cursor-pointer transition-all duration-200',
            'border-slate-200/60 dark:border-slate-700/60',
            'hover:border-slate-300 dark:hover:border-slate-600',
            'hover:shadow-lg',
            'group',
            className
          )}
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                  'transition-transform duration-200 group-hover:scale-105',
                  iconBgColor
                )}
              >
                <Icon className={cn('w-6 h-6', iconColor)} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                  {title}
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                  {description}
                </p>
              </div>

              {/* Arrow */}
              <ChevronRight
                className={cn(
                  'w-5 h-5 text-slate-400 dark:text-slate-500 flex-shrink-0',
                  'transition-transform duration-200',
                  'group-hover:translate-x-1 group-hover:text-violet-600 dark:group-hover:text-violet-400'
                )}
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </Link>
  );
}

// ============================================
// Grid Component
// ============================================

export function SettingsNavigationGrid({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid gap-4',
        'grid-cols-1 md:grid-cols-2',
        className
      )}
    >
      {children}
    </div>
  );
}
